import React, { useEffect, useState, useRef } from "react";
import cn from "classnames";
import activeSessionGreenIcon from "@src/assets/svg/active-session-green-icon.svg";
import disconnectSessionOrangeIcon from "@src/assets/svg/disconnect-session-orange-icon.svg";
import styles from "./vnc-countdown-bar.module.scss";
import { useI18N } from "@src/language/language-context";
import { formatDuration } from "@src/cloud-connect/vpn-manager/utils/format-duration-string";

interface CountdownBarProps {
  expiryDate?: string; // ISO string, e.g., "2025-10-23T18:16:47.079390Z"
  duration?: string; // ISO 8601 duration, e.g., "PT1H30M"
}

const cssClassName = "vnc-countdown-bar";

// Utility to parse ISO 8601 durations like PT1H30M to seconds
const parseISODuration = (duration: string): number => {
  const hours = parseFloat(duration.match(/(\d+)H/)?.[1] ?? "0");
  const minutes = parseFloat(duration.match(/T[^M]*?(\d+)M/)?.[1] ?? "0");
  const seconds = parseFloat(duration.match(/(\d+(?:\.\d+)?)S/)?.[1] ?? "0");

  return hours * 3600 + minutes * 60 + seconds;
};

export const VncCountdownBar = ({
  expiryDate,
  duration,
}: CountdownBarProps) => {
  const i18n = useI18N();
  const texts = {
    sessionStatusTextActive: i18n(
      "modules.vncCountdownBar.sessionStatusTextActive",
    ),
    sessionStatusTextCompleted: i18n(
      "modules.vncCountdownBar.sessionStatusTextCompleted",
    ),
  };

  const [widthPercent, setWidthPercent] = useState(100);
  const [timeText, setTimeText] = useState("");
  const [status, setStatus] = useState(texts.sessionStatusTextActive);
  const [completed, setCompleted] = useState(false);
  const totalTimeRef = useRef(0);

  useEffect(() => {
    let targetTime: number;

    if (expiryDate) {
      targetTime = new Date(expiryDate).getTime();
      totalTimeRef.current = Math.max(0, (targetTime - Date.now()) / 1000);
    } else if (duration) {
      const seconds = parseISODuration(duration);
      targetTime = Date.now() + seconds * 1000;
      totalTimeRef.current = seconds;
    } else {
      console.error("Either expiryDate or duration must be provided");
      return;
    }

    const update = () => {
      const now = Date.now();
      const diff = Math.max(0, (targetTime - now) / 1000);

      setWidthPercent((diff / totalTimeRef.current) * 100);
      setTimeText(formatDuration(Math.floor(diff)));

      if (diff <= 0) {
        setStatus(texts.sessionStatusTextCompleted);
        setCompleted(true);
        clearInterval(timer);
      }
    };

    const timer = setInterval(update, 100);
    update();

    return () => clearInterval(timer);
  }, [expiryDate, duration, texts.sessionStatusTextCompleted]);

  return (
    <div
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--completed`]]: completed,
      })}
    >
      <div className={styles[`${cssClassName}__status-container`]}>
        <img
          className={styles[`${cssClassName}__icon`]}
          src={completed ? disconnectSessionOrangeIcon : activeSessionGreenIcon}
          alt="Status Icon"
        />
        {status}
      </div>

      <div className={styles[`${cssClassName}__bar-container`]}>
        <div
          className={styles[`${cssClassName}__bar`]}
          style={{ width: `${widthPercent}%` }}
        />
      </div>

      <div
        className={cn(styles[`${cssClassName}__time`], {
          [styles[`${cssClassName}__time--completed`]]: completed,
        })}
      >
        {timeText}
      </div>
    </div>
  );
};
